import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Icon from "@/components/Icon";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/app", icon: "LayoutDashboard", label: "Dashboard", end: true },
  { to: "/app/qr-codes", icon: "LayoutGrid", label: "QR Codes" },
  { to: "/app/bulk", icon: "Layers", label: "Bulk Creator", cap: "qr.edit" },
  { to: "/app/templates", icon: "LayoutTemplate", label: "Templates" },
  { to: "/app/analytics", icon: "BarChart3", label: "Analytics" },
  { to: "/app/users", icon: "Users", label: "Users" },
  { to: "/app/settings", icon: "Building2", label: "Company" },
  { to: "/app/domain", icon: "Globe", label: "Custom Domain" },
  { to: "/app/profile", icon: "User", label: "My Profile" },
  { to: "/app/plan-usage", icon: "Gauge", label: "Plan & Usage", cap: "billing.view" },
  { to: "/app/billing", icon: "CreditCard", label: "Billing", cap: "billing.view" },
  { to: "/app/help", icon: "LifeBuoy", label: "Help" },
];

export default function AppLayout({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout, can, workspace, switchCompany } = useAuth();
  const mems = workspace?.memberships || [];
  useEffect(() => { const t = sessionStorage.getItem("pending_invite"); if (t && user) { sessionStorage.removeItem("pending_invite"); window.location.assign(`/invite/${t}`); } }, [user]);
  const [open, setOpen] = useState(false);
  const active = (n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to));

  const SideContent = () => (
    <div className="flex flex-col h-full w-full min-w-0 overflow-x-hidden">
      <Link to="/app" className="px-5 h-16 flex items-center border-b border-border"><Logo /></Link>
      {can("qr.edit") && <Link to="/app/builder" data-testid="sidebar-generate" className="mx-4 mt-4 rounded-xl bg-primary text-primary-foreground py-2.5 text-sm font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"><Icon name="Plus" className="h-4 w-4" />Generate QR</Link>}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto no-scrollbar">
        {mems.length > 1 && (
          <div className="px-1 pb-3">
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-2">Workspace</label>
            <select data-testid="company-switcher" title={mems.find((m) => m.company_id === workspace?.company_id)?.company_name} value={workspace?.company_id || ""} onChange={(e) => switchCompany(e.target.value)}
              className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm font-semibold">
              {mems.map((m) => <option key={m.company_id} value={m.company_id}>{m.company_name}</option>)}
            </select>
            <div className="text-[11px] text-muted-foreground px-2 mt-1" data-testid="switcher-role">Your role here: <span className="capitalize">{user?.role}</span></div>
          </div>
        )}
        {NAV.filter((n) => (!n.cap || can(n.cap)) && (user?.role || ["/app/profile", "/app/help"].includes(n.to))).map((n) => (
          <Link key={n.to} to={n.to} data-testid={`nav-${n.label.replace(/\s/g, "-").toLowerCase()}`} onClick={() => setOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${active(n) ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
            <Icon name={n.icon} className="h-4 w-4" />{n.label}
          </Link>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-9 w-9 rounded-full bg-primary/20 grid place-items-center text-sm font-bold text-primary overflow-hidden">{user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name || "U")[0]?.toUpperCase()}</div>
          <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate">{user?.name}</div><div className="text-xs text-muted-foreground truncate capitalize" data-testid="sidebar-role">{user?.role || "no workspace"}</div></div>
          <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-accent"><Icon name="LogOut" className="h-4 w-4" /></button>
        </div>
        {user?.is_platform_admin && (
          <Link to="/admin" data-testid="admin-panel-link" className="mt-1 flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-primary bg-primary/10 hover:bg-primary/15 transition-colors">
            <Icon name="ShieldCheck" className="h-4 w-4" />Admin Panel
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 border-r border-border bg-card/50 glass z-30 overflow-x-hidden">{SideContent()}</aside>
      {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setOpen(false)} />}
      <aside className={`lg:hidden fixed inset-y-0 left-0 w-64 max-w-[80vw] bg-card border-r border-border z-50 overflow-x-hidden transition-transform ${open ? "translate-x-0" : "-translate-x-full"}`}>{SideContent()}</aside>
      <div className="lg:pl-64">
        <header className="h-16 border-b border-border glass bg-background/80 sticky top-0 z-20 flex items-center justify-between px-4 lg:px-8">
          <button className="lg:hidden h-9 w-9 grid place-items-center rounded-xl border border-border" onClick={() => setOpen(true)}><Icon name="Menu" className="h-4 w-4" /></button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {can("qr.edit") && <Link to="/app/builder" data-testid="header-new-qr" className="rounded-xl bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:opacity-90 flex items-center gap-1.5"><Icon name="Plus" className="h-4 w-4" />New QR</Link>}
          </div>
        </header>
        <main className="p-4 lg:p-8">{user && !user.role && !loc.pathname.startsWith("/app/profile") ? (
          <div data-testid="no-workspace" className="max-w-lg mx-auto mt-16 rounded-2xl border border-border bg-card p-8 text-center">
            <Icon name="Building2" className="h-8 w-8 mx-auto text-muted-foreground" />
            <h1 className="text-lg font-bold mt-3">You're not a member of any workspace</h1>
            <p className="text-sm text-muted-foreground mt-1">Your access to the previous workspace was removed. Ask a workspace owner to invite you again. You can still manage your own profile.</p>
          </div>
        ) : children}</main>
      </div>
    </div>
  );
}
